import * as Yup from "yup";

// Plan Settings Validation
export const planSettingsValidation = Yup.object()
  .shape({
    billingCycle: Yup.string().required("Required"),
    paymentMethod: Yup.string().required("Required"),
    billingAddress: Yup.string().required("Required"),
    city: Yup.string().required("Required"),
    postalCode: Yup.string().nullable(),
    selectedPlan: Yup.object().nullable(),

    // Card-specific fields
    billingName: Yup.string().when("paymentMethod", {
      is: "card",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
    cardNumber: Yup.string().when("paymentMethod", {
      is: "card",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
    expiryDate: Yup.string().when("paymentMethod", {
      is: "card",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
    cvv: Yup.string().when("paymentMethod", {
      is: "card",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),

    // Mobile Money fields
    phoneNumber: Yup.string().when("paymentMethod", {
      is: "momo",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
    provider: Yup.string().when("paymentMethod", {
      is: "momo",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),

    // Bank transfer fields
    accountName: Yup.string().when("paymentMethod", {
      is: "bank",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
    accountNumber: Yup.string().when("paymentMethod", {
      is: "bank",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
    bankName: Yup.string().when("paymentMethod", {
      is: "bank",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),

    // Crypto fields
    cryptoType: Yup.string().when("paymentMethod", {
      is: "crypto",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
    walletAddress: Yup.string().when("paymentMethod", {
      is: "crypto",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.optional(),
    }),
  })
  .test(
    "at-least-one-payment-field",
    "Payment details are required",
    function (values) {
      const { paymentMethod } = values;

      switch (paymentMethod) {
        case "card":
          return !!(
            values.billingName &&
            values.cardNumber &&
            values.expiryDate &&
            values.cvv
          );
        case "momo":
          return !!(values.phoneNumber && values.provider);
        case "bank":
          return !!(
            values.accountName &&
            values.accountNumber &&
            values.bankName
          );
        case "crypto":
          return !!(values.cryptoType && values.walletAddress);
        default:
          return true;
      }
    }
  );

// Staff Creation Validation
export const usersPermissionsValidation = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be at most 50 characters")
    .matches(/^[a-zA-Z\s]*$/, "Name can only contain letters and spaces"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email address"),
  role_id: Yup.number().required("Role is required"),
  is_active: Yup.boolean().required("Status is required"),
  phone: Yup.string(),
  address: Yup.string(),
  password: Yup.string(),
});

// Role Creation Validation
export const rolesValidation = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be at most 50 characters")
    .matches(/^[a-zA-Z\s]*$/, "Name can only contain letters and spaces"),
  description: Yup.string(),
});

// Payment Settings Validation
export const paymentSettingsValidation = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  description: Yup.string().required("Description is required"),
  type: Yup.string().required("Payment type is required"),
});

// Shipping Settings Validation
export const shippingSettingsValidation = Yup.object().shape({
  defaultShippingMethod: Yup.object()
    .nullable()
    .required("Default shipping method is required"),
  processingTime: Yup.number()
    .min(0, "Processing time cannot be negative")
    .required("Processing time is required"),
  freeShippingEnabled: Yup.boolean(),
  freeShippingThreshold: Yup.number()
    .min(0, "Threshold cannot be negative")
    .when("freeShippingEnabled", {
      is: true,
      then: (schema) =>
        schema.required("Threshold is required when free shipping is enabled"),
    }),
  shippingZones: Yup.array()
    .min(1, "At least one shipping zone is required")
    .required("Shipping zones are required"),
  localShippingRate: Yup.number()
    .min(0, "Rate cannot be negative")
    .required("Local shipping rate is required"),
  domesticShippingRate: Yup.number()
    .min(0, "Rate cannot be negative")
    .required("Domestic shipping rate is required"),
  enableLocalPickup: Yup.boolean(),
  enableExpressDelivery: Yup.boolean(),
  pickupLocations: Yup.string().when("enableLocalPickup", {
    is: true,
    then: (schema) =>
      schema.required(
        "Pickup locations are required when local pickup is enabled"
      ),
  }),
});

// Tax Settings Validation
export const taxSettingsValidation = Yup.object().shape({
  taxCalculationMethod: Yup.object().required(
    "Tax calculation method is required"
  ),
  defaultTaxClass: Yup.object().required("Default tax class is required"),
  standardRate: Yup.string().required("Standard rate is required"),
  reducedRate: Yup.string().required("Reduced rate is required"),
  defaultDutyRate: Yup.string().when("calculateDuty", {
    is: true,
    then: (schema) =>
      schema.required("Duty rate is required when duty calculation is enabled"),
    otherwise: (schema) => schema.optional(),
  }),
});

// Branch Settings Validation
export const branchSettingsValidation = Yup.object().shape({
  branchName: Yup.string().required("Branch name is required"),
  branchCode: Yup.string().required("Branch code is required"),
  branchAddress: Yup.string().required("Branch address is required"),
  branchPhone: Yup.string().required("Branch phone is required"),
  branchEmail: Yup.string()
    .email("Invalid email")
    .required("Branch email is required"),
  managerName: Yup.string().required("Manager name is required"),
  managerPhone: Yup.string().required("Manager phone is required"),
  openingTime: Yup.string().required("Opening time is required"),
  closingTime: Yup.string().required("Closing time is required"),
  workingDays: Yup.array().min(1, "Select at least one working day"),
});

// Integration Settings Validation
export const integrationSettingsValidation = Yup.object().shape({
  selectedPlatforms: Yup.array(),
  platformApiKeys: Yup.object(),
  googleAnalyticsId: Yup.string(),
  webhookUrls: Yup.string(),
});

// Domain Settings Validation
export const domainSettingsValidation = Yup.object().shape({
  primaryDomain: Yup.string().required("Primary domain is required"),
  forceSSL: Yup.boolean(),
  autoRedirect: Yup.boolean(),
  domainAliases: Yup.string(),
});

// Notification Settings Validation
export const notificationSettingsValidation = Yup.object().shape({
  orderNotifications: Yup.boolean(),
  customerMessages: Yup.boolean(),
  stockAlerts: Yup.boolean(),
  securityAlerts: Yup.boolean(),
  notificationEmails: Yup.string().test(
    "emails",
    "Invalid email format",
    function (value) {
      if (!value) return true;
      const emails = value.split("\n").map((email) => email.trim());
      return emails.every(
        (email) =>
          !email || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
      );
    }
  ),
});

// Language Settings Validation
export const languageSettingsValidation = Yup.object().shape({
  defaultLanguage: Yup.object().required("Default language is required"),
  availableLanguages: Yup.array()
    .min(1, "At least one language must be enabled")
    .required("Available languages are required"),
  autoTranslate: Yup.boolean(),
  allowUserLanguage: Yup.boolean(),
  fallbackLanguage: Yup.object().required("Fallback language is required"),
});

// Privacy Settings Validation
export const privacySettingsValidation = Yup.object().shape({
  collectAnalytics: Yup.boolean(),
  cookieConsent: Yup.boolean(),
  customerDataRetention: Yup.object().required(
    "Customer data retention period is required"
  ),
  orderDataRetention: Yup.object().required(
    "Order data retention period is required"
  ),
  allowDataExport: Yup.boolean(),
  allowDataDeletion: Yup.boolean(),
  privacyContact: Yup.string()
    .email("Invalid email address")
    .required("Privacy contact email is required"),
});
